import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import PhoneInput from './PhoneInput';
import EditEventModal from './EditEventModal';
import EventAttendeesModal from './EventAttendeesModal';
import { validateName, validatePhone, extractPhoneDigits } from '../utils/validation';
import { formatDate } from '../utils/formatting';

// Helper function to get localized text from bilingual object
function getLocalizedText(textObj, lang = 'en') {
  if (typeof textObj === 'string') return textObj; // Legacy support
  if (typeof textObj === 'object' && textObj !== null) {
    return textObj[lang] || textObj.en || '';
  }
  return '';
}

// Haversine formula to calculate distance between two coordinates
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function EventsList() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith('tl') ? 'tl' : 'en';
  const { currentUser, isAdmin, canManageEvents } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signupStatus, setSignupStatus] = useState({});

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [maxDistance, setMaxDistance] = useState(50); // km
  const [nearMeActive, setNearMeActive] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Anonymous signup modal state
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [anonymousForm, setAnonymousForm] = useState({ name: '', phone: '+63' });
  const [anonymousFormErrors, setAnonymousFormErrors] = useState({ name: '', phone: '' });
  const [anonymousFormLoading, setAnonymousFormLoading] = useState(false);

  // Edit event modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [eventToEdit, setEventToEdit] = useState(null);

  // Attendees modal state
  const [isAttendeesModalOpen, setIsAttendeesModalOpen] = useState(false);
  const [eventForAttendees, setEventForAttendees] = useState(null);

  // Past events state
  const [showPastEvents, setShowPastEvents] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  // Get user location when "Near Me" is activated
  const getUserLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setLocationLoading(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setLocationLoading(false);
        setNearMeActive(true);
      },
      (error) => {
        let errorMessage = 'Unable to get your location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location permissions.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
          default:
            break;
        }
        setLocationError(errorMessage);
        setLocationLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  }, []);

  async function fetchEvents() {
    try {
      const response = await fetch('/api/events');
      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }
      const data = await response.json();
      setEvents(data);
      setIsLoading(false);
    } catch (error) {
      setError(error);
      setIsLoading(false);
    }
  }

  // Calculate distances for events with coordinates
  const eventsWithDistance = useMemo(() => {
    if (!userLocation) return events.map(e => ({ ...e, distance: null }));

    return events.map(event => {
      let distance = null;
      if (event.place?.lat && event.place?.lng) {
        distance = calculateDistance(
          userLocation.lat, userLocation.lng,
          event.place.lat, event.place.lng
        );
      }
      return { ...event, distance };
    });
  }, [events, userLocation]);

  // Filter and sort events
  const filteredEvents = useMemo(() => {
    let result = [...eventsWithDistance];

    // Text search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(event => {
        const name = getLocalizedText(event.name, currentLang);
        const desc = getLocalizedText(event.description, currentLang);
        const placeName = event.place?.name || event.place || '';
        return name.toLowerCase().includes(query) ||
          desc.toLowerCase().includes(query) ||
          placeName.toLowerCase().includes(query);
      });
    }

    // Date filter
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfWeek = new Date(today);
    endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    switch (dateFilter) {
      case 'today':
        result = result.filter(event => {
          const eventDate = new Date(event.start_time);
          return eventDate >= today && eventDate < new Date(today.getTime() + 86400000);
        });
        break;
      case 'week':
        result = result.filter(event => {
          const eventDate = new Date(event.start_time);
          return eventDate >= today && eventDate <= endOfWeek;
        });
        break;
      case 'month':
        result = result.filter(event => {
          const eventDate = new Date(event.start_time);
          return eventDate >= today && eventDate <= endOfMonth;
        });
        break;
      default:
        // Show all future events (events that haven't started yet)
        result = result.filter(event => new Date(event.start_time) >= now);
    }

    // Distance filter (only when Near Me is active)
    if (nearMeActive && userLocation) {
      result = result.filter(event =>
        event.distance === null || event.distance <= maxDistance
      );
    }

    // Sort
    switch (sortBy) {
      case 'date':
        result.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
        break;
      case 'name':
        result.sort((a, b) => {
          const nameA = getLocalizedText(a.name, currentLang);
          const nameB = getLocalizedText(b.name, currentLang);
          return nameA.localeCompare(nameB);
        });
        break;
      case 'distance':
        if (userLocation) {
          result.sort((a, b) => {
            if (a.distance === null && b.distance === null) return 0;
            if (a.distance === null) return 1;
            if (b.distance === null) return -1;
            return a.distance - b.distance;
          });
        }
        break;
      default:
        break;
    }

    return result;
  }, [eventsWithDistance, searchQuery, dateFilter, sortBy, nearMeActive, userLocation, maxDistance, currentLang]);

  // Past events computation
  const pastEvents = useMemo(() => {
    const now = new Date();
    const threeMonthsAgo = new Date(now);
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

    return eventsWithDistance
      .filter(event => {
        const eventDate = new Date(event.start_time);
        return eventDate < now && eventDate >= threeMonthsAgo;
      })
      .sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
  }, [eventsWithDistance]);

  // Helper to get slots info
  function getSlotsInfo(event) {
    const maxSlots = event.maxSlots || 5;
    const attendeesCount = event.attendees?.length || 0;
    const slotsRemaining = maxSlots - attendeesCount;
    const isFull = slotsRemaining <= 0;
    return { maxSlots, attendeesCount, slotsRemaining, isFull };
  }

  // Handle signup button click
  function handleSignupClick(eventId) {
    const event = events.find(e => e.id === eventId);
    const { isFull } = getSlotsInfo(event);

    if (isFull) {
      setSignupStatus(prev => ({
        ...prev,
        [eventId]: { error: 'This event is full' }
      }));
      return;
    }

    if (currentUser) {
      // Logged in user - direct signup
      handleAuthenticatedSignup(eventId);
    } else {
      // Not logged in - show anonymous signup modal
      setSelectedEventId(eventId);
      setAnonymousForm({ name: '', phone: '+63' });
      setAnonymousFormErrors({ name: '', phone: '' });
      setShowSignupModal(true);
    }
  }

  // Handle authenticated user signup
  async function handleAuthenticatedSignup(eventId) {
    try {
      setSignupStatus(prev => ({ ...prev, [eventId]: { loading: true } }));

      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/events/${eventId}/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: currentUser.uid,
          email: currentUser.email,
          isAnonymous: false
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to sign up');
      }

      setSignupStatus(prev => ({
        ...prev,
        [eventId]: { success: true, message: 'Successfully signed up!' }
      }));

      // Refresh events to update slot count
      fetchEvents();
    } catch (error) {
      setSignupStatus(prev => ({
        ...prev,
        [eventId]: { error: error.message }
      }));
    }
  }

  // Handle anonymous signup form submission
  async function handleAnonymousSignup(e) {
    e.preventDefault();

    // Validate all fields
    const nameValidation = validateName(anonymousForm.name);
    const phoneValidation = validatePhone(anonymousForm.phone);

    const errors = {
      name: nameValidation.error || '',
      phone: phoneValidation.error || ''
    };

    setAnonymousFormErrors(errors);

    // Check if any validation failed
    if (!nameValidation.isValid || !phoneValidation.isValid) {
      return;
    }

    try {
      setAnonymousFormLoading(true);
      setAnonymousFormErrors({ name: '', phone: '' });

      // Extract just the digits for storage
      const phoneDigits = extractPhoneDigits(anonymousForm.phone);

      const response = await fetch(`/api/events/${selectedEventId}/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: anonymousForm.name.trim(),
          phone: phoneDigits, // Store as digits (639171234567)
          phoneFormatted: anonymousForm.phone, // Also store formatted version
          isAnonymous: true
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to sign up');
      }

      setSignupStatus(prev => ({
        ...prev,
        [selectedEventId]: { success: true, message: 'Successfully signed up!' }
      }));

      setShowSignupModal(false);
      setAnonymousForm({ name: '', phone: '+63' });

      // Refresh events to update slot count
      fetchEvents();
    } catch (error) {
      setAnonymousFormErrors(prev => ({ ...prev, general: error.message }));
    } finally {
      setAnonymousFormLoading(false);
    }
  }

  function handleNearMeToggle() {
    if (nearMeActive) {
      setNearMeActive(false);
    } else if (userLocation) {
      setNearMeActive(true);
    } else {
      getUserLocation();
    }
  }

  function clearFilters() {
    setSearchQuery('');
    setDateFilter('all');
    setSortBy('date');
    setNearMeActive(false);
    setMaxDistance(50);
  }

  function handleEditClick(event, e) {
    e.stopPropagation();
    setEventToEdit(event);
    setIsEditModalOpen(true);
  }

  const renderEventCard = (event, isPast = false) => {
    const { maxSlots, attendeesCount, slotsRemaining, isFull } = getSlotsInfo(event);

    const handleCardClick = () => {
      navigate(`/events/${event.id}`);
    };

    return (
      <div
        className={`card-modern card-clickable ${isPast ? 'card-past' : ''}`}
        key={event.id}
        onClick={handleCardClick}
        role="article"
        tabIndex={0}
        onKeyPress={(e) => e.key === 'Enter' && handleCardClick()}
      >
        {/* Admin/Leader Edit Button */}
        {canManageEvents && (
          <div className="card-admin-btns">
            <button
              className="card-manage-btn"
              title={t('admin_manage_event')}
              onClick={(e) => {
                e.stopPropagation();
                setEventForAttendees(event);
                setIsAttendeesModalOpen(true);
              }}
            >
              👥
            </button>
            <button
              className="card-edit-btn"
              onClick={(e) => handleEditClick(event, e)}
              title={t('edit')}
            >
              ✏️
            </button>
          </div>
        )}
        <h5 className="card-title-modern">{getLocalizedText(event.name, currentLang)}</h5>
        <p className="card-text-modern">{getLocalizedText(event.description, currentLang)}</p>
        <div className="card-meta" style={{ marginTop: 'auto' }}>
          <div className="card-meta-item">
            <span className="card-meta-label">{t('when')}</span>
            <span>{event.start_time ? formatDate(event.start_time, currentLang) : 'TBD'}</span>
          </div>
          <div className="card-meta-item">
            <span className="card-meta-label">{t('where')}</span>
            <span>{event.place?.name || event.place || 'TBD'}</span>
          </div>
          {event.place?.address && (
            <div className="card-meta-item">
              <span className="card-meta-label">📍 {t('address')}</span>
              <span>{event.place.address}</span>
            </div>
          )}
          {event.distance !== null && nearMeActive && (
            <div className="card-meta-item distance-item">
              <span className="card-meta-label">📍 {t('distance')}</span>
              <span className="distance-value">
                {event.distance < 1
                  ? `${Math.round(event.distance * 1000)}${t('m_away')}`
                  : `${event.distance.toFixed(1)}${t('km_away')}`
                }
              </span>
            </div>
          )}
        </div>

        {/* Slots indicator */}
        <div className={`slots-indicator ${isFull ? 'full' : slotsRemaining <= 2 ? 'low' : ''}`}>
          <span className="slots-icon">🎟️</span>
          <span className="slots-text">
            {isFull
              ? t('events_slots_full')
              : `${slotsRemaining} / ${maxSlots} ${t('events_slots_available')}`
            }
          </span>
          <div className="slots-bar">
            <div
              className="slots-bar-fill"
              style={{ width: `${(attendeesCount / maxSlots) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Card Footer with View Details link */}
        <div className="card-footer-actions">
          <Link
            to={`/events/${event.id}`}
            className="view-details-link"
            onClick={(e) => e.stopPropagation()}
          >
            {t('event_view_details')} →
          </Link>

          {signupStatus[event.id]?.success ? (
            <div className="success-badge" onClick={(e) => e.stopPropagation()}>✓ {t('events_signup_success')}</div>
          ) : signupStatus[event.id]?.error ? (
            <div className="error-badge" onClick={(e) => e.stopPropagation()}>{signupStatus[event.id].error}</div>
          ) : isPast ? (
            <button
              className="btn-accent-modern btn-primary-modern btn-disabled"
              disabled
              onClick={(e) => e.stopPropagation()}
            >
              {t('event_ended')}
            </button>
          ) : (
            <button
              className={`btn-accent-modern btn-primary-modern ${isFull ? 'btn-disabled' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleSignupClick(event.id);
              }}
              disabled={signupStatus[event.id]?.loading || isFull}
            >
              {signupStatus[event.id]?.loading
                ? t('events_signing_up')
                : isFull
                  ? t('events_slots_full')
                  : t('events_signup_btn')
              }
            </button>
          )}
        </div>
      </div>
    );
  };

  const hasActiveFilters = searchQuery || dateFilter !== 'all' || nearMeActive;

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p className="loading-text">{t('loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-icon">⚠️</div>
        <p className="error-message">Error: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="page-section">
      <div className="section-header">
        <div>
          <h2 className="section-title">{t('upcoming_events')}</h2>
          <p className="section-subtitle">{t('events_subtitle')}</p>
        </div>
        {canManageEvents && (
          <Link to="/create-event" className="btn-accent-modern btn-primary-modern">
            {t('events_create_btn')}
          </Link>
        )}
      </div>

      {/* Search and Filter Section */}
      <div className="search-filter-section">
        {/* Search Bar */}
        <div className="search-bar-container">
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder={t('events_search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="search-clear-btn"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          <button
            className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <span className="filter-icon">⚙️</span>
            {t('events_filters')}
            {hasActiveFilters && <span className="filter-badge"></span>}
          </button>
        </div>

        {/* Filter Options Panel */}
        <div className={`filter-panel ${showFilters ? 'open' : ''}`}>
          <div className="filter-row">
            {/* Near Me Location Filter */}
            <div className="filter-group">
              <label className="filter-label">📍 {t('events_filter_location')}</label>
              <button
                className={`near-me-btn ${nearMeActive ? 'active' : ''}`}
                onClick={handleNearMeToggle}
                disabled={locationLoading}
              >
                {locationLoading ? (
                  <>
                    <span className="btn-spinner"></span>
                    {t('events_finding_location')}
                  </>
                ) : nearMeActive ? (
                  <>📍 {t('events_near_me_active')}</>
                ) : (
                  <>📍 {t('events_find_near_me')}</>
                )}
              </button>
              {locationError && (
                <p className="filter-error">{locationError}</p>
              )}
              {nearMeActive && userLocation && (
                <div className="distance-slider-container">
                  <label className="distance-label">
                    {t('events_within')} {maxDistance} km
                  </label>
                  <input
                    type="range"
                    min="5"
                    max="200"
                    value={maxDistance}
                    onChange={(e) => setMaxDistance(Number(e.target.value))}
                    className="distance-slider"
                  />
                  <div className="distance-marks">
                    <span>5km</span>
                    <span>100km</span>
                    <span>200km</span>
                  </div>
                </div>
              )}
            </div>

            {/* Date Filter */}
            <div className="filter-group">
              <label className="filter-label">📅 {t('events_filter_date')}</label>
              <div className="filter-chips">
                {[
                  { value: 'all', label: t('events_filter_all') },
                  { value: 'today', label: t('events_filter_today') },
                  { value: 'week', label: t('events_filter_week') },
                  { value: 'month', label: t('events_filter_month') }
                ].map(option => (
                  <button
                    key={option.value}
                    className={`filter-chip ${dateFilter === option.value ? 'active' : ''}`}
                    onClick={() => setDateFilter(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sort By */}
            <div className="filter-group">
              <label className="filter-label">↕️ {t('events_sort_by')}</label>
              <select
                className="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="date">{t('events_sort_date')}</option>
                <option value="name">{t('events_sort_name')}</option>
                {userLocation && <option value="distance">{t('events_sort_distance')}</option>}
              </select>
            </div>
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button className="clear-filters-btn" onClick={clearFilters}>
              ✕ {t('events_clear_filters')}
            </button>
          )}
        </div>

        {/* Results Summary */}
        <div className="results-summary">
          <span className="results-count">
            {filteredEvents.length} {filteredEvents.length === 1 ? t('event') : t('events_lower')} {t('events_found')}
          </span>
          {nearMeActive && userLocation && (
            <span className="location-active-badge">
              📍 {t('events_showing_within')} {maxDistance}km
            </span>
          )}
        </div>
      </div>

      {filteredEvents.length === 0 ? (
        <div className="empty-state">
          {hasActiveFilters ? (
            <>
              <p>{t('events_no_match')}</p>
              <button className="btn-outline-modern" onClick={clearFilters}>
                {t('events_clear_filters')}
              </button>
            </>
          ) : (
            <p>{t('events_no_events_yet')} {isAdmin && <Link to="/create-event">{t('events_create_first')}</Link>}</p>
          )}
        </div>
      ) : (
        <div className="grid-container">
          {filteredEvents.map(event => renderEventCard(event))}
        </div>
      )}

      {/* Past Events Section */}
      {pastEvents.length > 0 && (
        <div className="section-divider">
          <div
            className={`past-events-banner ${showPastEvents ? 'active' : ''}`}
            onClick={() => setShowPastEvents(!showPastEvents)}
          >
            <div className="past-events-header-content">
              <div className="past-events-icon-wrapper">
                🕰️
              </div>
              <div className="past-events-text-content">
                <h3 className="past-events-title">{t('past_events_title')}</h3>
                <p className="past-events-subtitle-text">{t('past_events_subtitle')}</p>
              </div>
            </div>
            <div className={`past-events-toggle-icon ${showPastEvents ? 'rotated' : ''}`}>
              ▼
            </div>
          </div>

          {showPastEvents && (
            <div className="grid-container animate-fade-in past-events-grid">
              {pastEvents.map(event => renderEventCard(event, true))}
            </div>
          )}
        </div>
      )}


      {/* Anonymous Signup Modal */}
      {showSignupModal && (
        <div className="modal-overlay" onClick={() => setShowSignupModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close-btn"
              onClick={() => setShowSignupModal(false)}
              aria-label="Close modal"
            >
              ✕
            </button>

            <h3 className="modal-title">🎟️ {t('events_modal_title')}</h3>
            <p className="modal-subtitle">{t('events_modal_subtitle')}</p>

            {anonymousFormErrors.general && (
              <div className="error-banner">{anonymousFormErrors.general}</div>
            )}

            <form onSubmit={handleAnonymousSignup} className="modal-form">
              <div className={`form-group ${anonymousFormErrors.name ? 'has-error' : ''}`}>
                <label htmlFor="signup-name">{t('events_your_name')} <span className="required-indicator">*</span></label>
                <input
                  type="text"
                  id="signup-name"
                  className={`form-input ${anonymousFormErrors.name ? 'error' : ''}`}
                  value={anonymousForm.name}
                  onChange={(e) => {
                    setAnonymousForm(prev => ({ ...prev, name: e.target.value }));
                    if (anonymousFormErrors.name) {
                      setAnonymousFormErrors(prev => ({ ...prev, name: '' }));
                    }
                  }}
                  placeholder={t('events_enter_full_name')}
                  autoFocus
                  maxLength={100}
                />
                {anonymousFormErrors.name && (
                  <span className="field-error">{anonymousFormErrors.name}</span>
                )}
              </div>

              <PhoneInput
                id="signup-phone"
                value={anonymousForm.phone}
                onChange={(phone) => {
                  setAnonymousForm(prev => ({ ...prev, phone }));
                  if (anonymousFormErrors.phone) {
                    setAnonymousFormErrors(prev => ({ ...prev, phone: '' }));
                  }
                }}
                required
                showWhatsApp={true}
                label={t('events_phone_label')}
              />
              {anonymousFormErrors.phone && (
                <span className="field-error" style={{ marginTop: '-0.5rem' }}>{anonymousFormErrors.phone}</span>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-outline-modern"
                  onClick={() => setShowSignupModal(false)}
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="btn-primary-modern"
                  disabled={anonymousFormLoading}
                >
                  {anonymousFormLoading ? t('events_signing_up') : t('events_confirm_signup')}
                </button>
              </div>
            </form>

            <p className="modal-footer-text">
              {t('auth_already_have_account')} <Link to="/login" onClick={() => setShowSignupModal(false)}>{t('auth_login_link')}</Link> {t('events_login_faster')}
            </p>
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      <EditEventModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEventToEdit(null);
        }}
        event={eventToEdit}
        onSuccess={() => {
          fetchEvents();
        }}
      />

      {/* Event Attendees Modal */}
      <EventAttendeesModal
        isOpen={isAttendeesModalOpen}
        onClose={() => {
          setIsAttendeesModalOpen(false);
          setEventForAttendees(null);
        }}
        event={eventForAttendees}
        onSuccess={() => {
          fetchEvents();
        }}
      />
    </div>
  );
}

export default EventsList;
